/**
 * Admin Route Guard - Detachable Admin Module
 * 
 * This component protects admin routes and handles admin-specific redirects.
 * When extracting to a separate app, this component can be moved with minimal changes.
 */

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAdminAuth } from '../hooks/useAdminAuth';
import { buildLoginUrlWithRedirect } from '../../utils/redirectUtils';

interface AdminRouteProps {
  children: React.ReactElement;
}

/**
 * Route guard component that ensures only admin users can access admin routes
 * Authentication is handled by this component, so child components don't need to check auth
 */
export const AdminRoute: React.FC<AdminRouteProps> = ({ children }) => {
  const { isAdmin, hasAdminRole, isLoading, user } = useAdminAuth();
  const location = useLocation();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Verifying admin access...</p>
        </div>
      </div>
    );
  }

  // If not authenticated, redirect to login with redirectTo parameter
  if (!user) {
    const currentPath = location.pathname + location.search + (location.hash || '');
    const loginUrl = buildLoginUrlWithRedirect(currentPath);
    return <Navigate to={loginUrl} replace />;
  }

  // If authenticated but doesn't have admin role, redirect to regular dashboard
  // We check hasAdminRole instead of isAdmin to allow auto-switch to work
  // The useAdminAuth hook will auto-switch activeRole to 'admin' if hasAdminRole is true
  if (!hasAdminRole) {
    return <Navigate to="/dashboard" replace />;
  }

  // User is authenticated and has admin role (activeRole will be auto-switched to 'admin')
  // Render the protected component
  return children;
};

export default AdminRoute;

